<template>
    <div class="p-4">
        <AccionesCabeceraPagina>
            <div class="d-flex gap-2">
                <button class="btn btn-sm btn-outline-success" :disabled="exportandoExcel" @click="exportarExcel">
                    <span v-if="exportandoExcel" class="spinner-border spinner-border-sm me-md-1" role="status" aria-hidden="true" />
                    <i v-else class="bi bi-file-earmark-excel me-md-1" />
                    <span class="d-none d-md-inline">{{ exportandoExcel ? 'Exportando...' : 'Exportar Excel' }}</span>
                </button>
                <button class="btn btn-sm btn-primary">
                    <i class="bi bi-plus-lg me-md-1" />
                    <span class="d-none d-md-inline">Nuevo cliente</span>
                </button>
            </div>
        </AccionesCabeceraPagina>

        <!-- Filtros plegables -->
        <div class="card mb-3">
            <div
                class="card-header d-flex align-items-center justify-content-between py-2 cursor-pointer"
                style="cursor: pointer;"
                @click="filtrosAbiertos = !filtrosAbiertos"
            >
                <span class="fw-semibold small">
                    <i class="bi bi-funnel me-1" />
                    Filtros
                </span>
                <i :class="filtrosAbiertos ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi small text-muted" />
            </div>

            <div v-show="filtrosAbiertos" class="card-body py-2">
                <div class="row g-2 align-items-end">
                    <div class="col-12 col-md-4">
                        <label class="form-label small mb-1">Buscar</label>
                        <input
                            v-model="search"
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="Nombre, NIF, email..."
                            @input="onSearchInput"
                        />
                    </div>
                    <div class="col-12 col-md-3">
                        <label class="form-label small mb-1">Activo</label>
                        <select v-model="activo" class="form-select form-select-sm" @change="actualizarUrl(1)">
                            <option value="">Todos</option>
                            <option value="1">Sí</option>
                            <option value="0">No</option>
                        </select>
                    </div>
                    <div class="col-auto">
                        <button class="btn btn-sm btn-secondary" @click="limpiarFiltros">
                            Limpiar
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tabla -->
        <div class="card">
            <div class="card-body p-0">
                <div v-if="errorExportacion" class="alert alert-danger m-3 mb-0">
                    {{ errorExportacion }}
                </div>

                <!-- Loading -->
                <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border spinner-border-sm text-secondary" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                </div>

                <!-- Error -->
                <div v-else-if="error" class="alert alert-danger m-3">
                    {{ error }}
                </div>

                <!-- Empty -->
                <div v-else-if="resultado && resultado.items.length === 0" class="text-center text-muted py-5 small">
                    No se encontraron clientes.
                </div>

                <!-- Datos -->
                <template v-else-if="resultado">
                    <!-- Cards (mobile) -->
                    <div class="d-md-none overflow-hidden">
                        <RouterLink
                            v-for="cliente in resultado.items"
                            :key="cliente.idc"
                            :to="{ name: 'cliente-detail', params: { idc: cliente.idc } }"
                            class="border-bottom px-3 py-2 text-decoration-none d-block text-dark"
                        >
                            <div class="fw-semibold small" v-html="highlight(cliente.nombreFiscal, search)" />
                            <div v-if="cliente.direccion || cliente.poblacion" class="small text-muted" v-html="highlight(direccionCompleta(cliente), search)" />
                            <div class="d-flex gap-3 mt-1">
                                <span v-if="cliente.telefono" class="small" v-html="highlight(cliente.telefono, search)" />
                                <span v-if="cliente.email" class="small text-muted text-clamp-2" v-html="highlight(cliente.email, search)" />
                            </div>
                        </RouterLink>
                    </div>

                    <!-- Tabla (desktop) -->
                    <div class="d-none d-md-block table-responsive">
                        <table class="table table-hover table-sm mb-0">
                            <thead class="table-light">
                                <tr>
                                    <th class="ps-3 border-0 small">Nombre Fiscal</th>
                                    <th class="d-none d-md-table-cell border-0 small">Dirección</th>
                                    <th class="border-0 small">Teléfono</th>
                                    <th class="d-none d-sm-table-cell border-0 small">Email</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="cliente in resultado.items"
                                    :key="cliente.idc"
                                >
                                    <td class="ps-0 small border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'cliente-detail', params: { idc: cliente.idc } }" class="text-decoration-none text-dark ps-3 py-2 d-block stretched-link" v-html="highlight(cliente.nombreFiscal, search)" />
                                    </td>
                                    <td class="d-none d-md-table-cell small border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'cliente-detail', params: { idc: cliente.idc } }" class="text-decoration-none text-dark py-2 d-block stretched-link" v-html="highlight(direccionCompleta(cliente), search)" />
                                    </td>
                                    <td class="small border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'cliente-detail', params: { idc: cliente.idc } }" class="text-decoration-none text-dark py-2 d-block stretched-link" v-html="highlight(cliente.telefono, search)" />
                                    </td>
                                    <td class="d-none d-sm-table-cell small border-0 py-0 position-relative">
                                        <RouterLink :to="{ name: 'cliente-detail', params: { idc: cliente.idc } }" class="text-decoration-none text-dark py-2 d-block stretched-link" v-html="highlight(cliente.email, search)" />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Paginación -->
                    <div class="d-flex align-items-center justify-content-between px-3 py-2 border-top">
                        <span class="small text-muted">
                            {{ resultado.totalCount }} registros &middot; Página {{ resultado.page }} de {{ resultado.totalPages }}
                        </span>
                        <div class="d-flex gap-1">
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="resultado.page <= 1"
                                @click="cambiarPagina(resultado.page - 1)"
                            >
                                <i class="bi bi-chevron-left" />
                            </button>
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="resultado.page >= resultado.totalPages"
                                @click="cambiarPagina(resultado.page + 1)"
                            >
                                <i class="bi bi-chevron-right" />
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import AccionesCabeceraPagina from '@/components/AccionesCabeceraPagina.vue'
import { usePageHeader } from '@/composables/usePageHeader'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'

usePageHeader('Clientes')
useBreadcrumbs([{ label: 'Clientes' }])
import { useRoute, useRouter } from 'vue-router'
import { clientesApi } from '@/api/clientesApi'
import type { Cliente, PaginatedResponse } from '@/types/cliente'
import { highlight } from '@/utils/highlight'

const PAGE_SIZE = 20

const route = useRoute()
const router = useRouter()

const filtrosAbiertos = ref(window.innerWidth >= 768)
const search = ref((route.query.search as string) ?? '')
const activo = ref((route.query.activo as string) ?? '1')
const loading = ref(false)
const error = ref<string | null>(null)
const errorExportacion = ref<string | null>(null)
const exportandoExcel = ref(false)
const resultado = ref<PaginatedResponse<Cliente> | null>(null)

let debounceTimer: ReturnType<typeof setTimeout> | null = null


function onSearchInput() {
    // Evita relanzar la navegación en cada pulsación mientras el usuario escribe.
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => actualizarUrl(1), 400)
}

function limpiarFiltros() {
    search.value = ''
    activo.value = ''
    actualizarUrl(1)
}

function cambiarPagina(pagina: number) {
    actualizarUrl(pagina)
}

function actualizarUrl(pagina: number) {
    // La URL actúa como fuente de verdad para filtros y paginación.
    router.push({
        query: {
            ...(search.value ? { search: search.value } : {}),
            activo: activo.value,
            ...(pagina > 1 ? { page: String(pagina) } : {}),
        },
    })
}

function direccionCompleta(cliente: Cliente): string {
    const partes = [cliente.direccion, cliente.poblacion].filter(Boolean)
    return partes.length > 0 ? partes.join(', ') : '—'
}


async function exportarExcel() {
    exportandoExcel.value = true
    errorExportacion.value = null

    try {
        const { archivo, nombreArchivo } = await clientesApi.exportarExcel({
            search: search.value || undefined,
            activo: activo.value !== '' ? Number(activo.value) : undefined,
        })

        const url = window.URL.createObjectURL(archivo)
        const enlace = document.createElement('a')
        enlace.href = url
        enlace.download = nombreArchivo
        document.body.appendChild(enlace)
        enlace.click()
        enlace.remove()
        window.URL.revokeObjectURL(url)
    } catch {
        errorExportacion.value = 'No se pudo exportar el Excel.'
    } finally {
        exportandoExcel.value = false
    }
}

async function cargar() {
    const pagina = Number(route.query.page) || 1
    loading.value = true
    error.value = null
    try {
        resultado.value = await clientesApi.getAll({
            search: search.value || undefined,
            activo: activo.value !== '' ? Number(activo.value) : undefined,
            page: pagina,
            pageSize: PAGE_SIZE,
        })
    } catch {
        error.value = 'No se pudieron cargar los clientes.'
    } finally {
        loading.value = false
    }
}

watch(
    () => route.query,
    (q) => {
        // Rehidrata el estado local cuando cambian filtros o página desde la ruta.
        search.value = (q.search as string) ?? ''
        activo.value = (q.activo as string) ?? '1'
        cargar()
    },
    { immediate: true },
)
</script>

<style scoped>
.text-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
